import React from "react";

// Source
import AzureAuthenticationContext from "../azure/azure-authentication-context";
import StoreRoute from "../store/Route";
import StoreLocalization from "../store/Localization";
import StoreSetting from "../store/Setting";
import ControllerAlert from "./Alert";
import * as HelperSrc from "../HelperSrc";
import * as ModelRoute from "../model/Route";
import * as ModelUser from "../model/User";
import * as ModelLocalization from "../model/Localization";
import * as ModelSetting from "../model/Setting";
import * as ApiAuthentication from "../api/Authentication";

export default class ControllerAuthentication {
    // Variable
    private storeSetting: ModelSetting.Istore<ModelSetting.Idata>;
    private storageSettingData = React.useRef<ModelSetting.Idata | undefined>(undefined);
    private storeRoute: ModelRoute.Istore<ModelRoute.Idata>;
    private storageRouteData = React.useRef<ModelRoute.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelLocalization.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementUsername = React.useRef<HTMLInputElement | null>(null);
    private elementPassword = React.useRef<HTMLInputElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);

    private mode = React.useRef("");
    private isRendered = React.useRef<boolean>(false);

    getStorageSetting: ModelSetting.Idata | undefined;

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    // Method
    constructor(mode: string, isRendered: boolean) {
        this.storeSetting = new StoreSetting();
        this.storageSettingData.current = this.storeSetting.storageGet();
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.mode.current = mode;
        this.isRendered.current = isRendered;

        const [getStorageSetting] = React.useState<ModelSetting.Idata | undefined>(this.storageSettingData.current);
        this.getStorageSetting = getStorageSetting;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_login", () => {
                this.initializeHtmlElement();
            });

            if (this.mode.current === "logout") {
                this.onLogout();
            } else {
                const cookieSessionExpired = HelperSrc.readCookie<string>("session_expired");

                if (cookieSessionExpired === "") {
                    if (this.storageLocalizationData.current) {
                        this.controllerAlert.show("error", this.storageLocalizationData.current.session_expired, false);
                    }

                    window.history.replaceState({}, "");

                    HelperSrc.removeCookie("session_expired");
                }
            }
        }, []);

        React.useEffect(() => {
            this.storageSettingData.current = this.storeSetting.storageGet();
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
        }, [this.storageSettingData.current, this.storageRouteData.current, this.storageLocalizationData.current]);
    };

    clickBasic = (): void => {
        const email = this.elementUsername.current ? this.elementUsername.current.value : "";
        const password = this.elementPassword.current ? this.elementPassword.current.value : "";

        this.setApiLoadingList((prev) => ({
            ...prev,
            authentication: true
        }));

        ApiAuthentication.loginBasic(email, password, (response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                this.setApiLoadingList((prev) => ({
                    ...prev,
                    authentication: false
                }));

                return;
            }

            this.controllerAlert.show("success", response.message.success, true, true, true, () => {
                if (this.storageRouteData.current) {
                    HelperSrc.routerHistoryPush(this.storageRouteData.current.main.path);
                }
            });
        });
    };

    clickAd = (): void => {
        const ua = window.navigator.userAgent;
        const msie = ua.indexOf("MSIE ");
        const msie11 = ua.indexOf("Trident/");
        const isIE = msie > 0 || msie11 > 0;
        const logInType = isIE ? "loginRedirect" : "loginPopup";

        const adModule = new AzureAuthenticationContext(this.controllerAlert);

        this.setApiLoadingList((prev) => ({
            ...prev,
            authentication: true
        }));

        adModule.login(logInType, (response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                this.setApiLoadingList((prev) => ({
                    ...prev,
                    authentication: false
                }));

                return;
            }

            this.controllerAlert.show("success", response.message.success, true, true, true, () => {
                if (this.storageRouteData.current) {
                    HelperSrc.routerHistoryPush(this.storageRouteData.current.main.path);
                }
            });
        });
    };

    onLogout = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            authentication: true
        }));

        ApiAuthentication.logout((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                this.setApiLoadingList((prev) => ({
                    ...prev,
                    authentication: false
                }));

                return;
            }

            const result = response.data.result as unknown as ModelUser.Idata;

            if (result) {
                if (result.auth_type === "basic") {
                    if (response.message.success) {
                        this.controllerAlert.show("success", response.message.success, true, true, true, () => {
                            this.redirectToLogin();
                        });
                    } else {
                        this.redirectToLogin();
                    }
                } else if (result.auth_type === "ad") {
                    const adModule = new AzureAuthenticationContext(this.controllerAlert);
                    const result = adModule.logout();

                    if (result === null) {
                        this.redirectToLogin();
                    } else {
                        result
                            .then(() => {
                                //...
                            })
                            .catch(() => {
                                //...
                            });
                    }
                } else {
                    this.redirectToLogin();
                }
            } else {
                const adModule = new AzureAuthenticationContext(this.controllerAlert);
                const result = adModule.logout();

                if (result === null) {
                    this.redirectToLogin();
                } else {
                    result
                        .then(() => {
                            //...
                        })
                        .catch(() => {
                            //...
                        });
                }
            }
        });
    };

    clickClearApplication = (): void => {
        ApiAuthentication.clearApplication((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            for (const localKey of Object.keys(localStorage)) {
                if (localKey.startsWith(`${HelperSrc.LABEL}_`)) {
                    const tag = localKey.substring(`${HelperSrc.LABEL}_`.length);

                    HelperSrc.removeStorage(tag);
                }
            }

            this.controllerAlert.show("success", response.message.success, false, false, false);

            if (this.storageRouteData.current) {
                HelperSrc.routerHistoryPush(this.storageRouteData.current.login.path);
            }
        });
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementUsername.current = document.querySelector<HTMLInputElement>("[name='username']");
            this.elementPassword.current = document.querySelector<HTMLInputElement>("[name='password']");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_login");
        }
    };

    private redirectToLogin = (): void => {
        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.login.path);
        }
    };
}
